import { Box, Checkbox, FormControlLabel } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import SearchItem from "components/SearchItem";
import type { AutocompleteOption as SearchItemAutocompleteOption } from "components/SearchItem";
import { getUserType } from "util/auth";
import { USER_TYPE_LIST } from "constants/user-type";
import "dayjs/locale/ja";
import { QuotationQuery } from "features/quotation/types/quotation";

interface AutocompleteOption {
  label: string;
  value: string | number;
}

interface SearchContainerProps {
  params: QuotationQuery;
  setParams: React.Dispatch<React.SetStateAction<QuotationQuery>>;
  quoteByList: AutocompleteOption[];
}

/**
 * A React functional component that renders a search container with multiple search boxes
 * and filters for a quotation feature. The component is designed to be responsive and
 * supports various screen sizes.
 * @param {SearchContainerProps} props - The props for the `SearchContainer` component.
 * @param {object} props.params - The current search parameters.
 * @param {Function} props.setParams - A function to update the search parameters.
 * @param {Function} props.onSearch - A callback function triggered when a search is performed.
 * @returns The rendered search container component.
 */
export default function SearchContainer({ params, setParams, quoteByList }: SearchContainerProps) {
  // Common layout constants to avoid recreating objects on each render
  const itemContainerWidth = useMemo(
    () => ({
      width: {
        xs: "100%",
        lg: "calc((100% - 10px) / 2)",
        xl: "calc((100% - 20px) / 3)",
      },
    }),
    [],
  );

  const dateRowSx = useMemo(
    () => ({
      display: "flex",
      gap: { xs: "2px", lg: "10px" },
    }),
    [],
  );

  const hiddenLabelSx = useMemo(
    () => ({ display: "none", minWidth: 0, minHeight: 0, p: 0, backgroundColor: "transparent" }),
    [],
  );

  const dateValueSx = useMemo(() => ({ width: "auto", minWidth: "185px" }), []);

  // Handlers
  const handleTextChange = useCallback(
    (key: keyof QuotationQuery) => (value: unknown) => {
      setParams((prev) => ({ ...prev, [key]: String(value) }));
    },
    [setParams],
  );

  const handleQuoteByChange = useCallback(
    (value: unknown) => {
      const selected = value as SearchItemAutocompleteOption | null;
      setParams((prev) => ({ ...prev, quote_by: selected?.label || "" }));
    },
    [setParams],
  );

  const handleStartDateChange = useCallback(
    (val: unknown) => {
      setParams((prev) => {
        const newStartDate = val ? String(val) : undefined;
        return {
          ...prev,
          created_at_start: newStartDate,
          created_at_end:
            prev.created_at_end &&
            newStartDate &&
            dayjs(newStartDate).isAfter(dayjs(prev.created_at_end))
              ? undefined
              : prev.created_at_end,
        };
      });
    },
    [setParams],
  );

  const handleEndDateChange = useCallback(
    (val: unknown) => {
      if (!params.created_at_start) {
        setParams((prev) => ({ ...prev, created_at_end: undefined }));
        return;
      }
      setParams((prev) => ({ ...prev, created_at_end: val ? String(val) : undefined }));
    },
    [params.created_at_start, setParams],
  );

  const handleExpiredToggle = useCallback(
    (_event: React.SyntheticEvent<Element, Event>, checked: boolean) => {
      setParams((prev) => ({ ...prev, expired_flg: checked ? 1 : 0 }));
    },
    [setParams],
  );
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: "10px", rowGap: "3px" }}>
      <SearchItem
        label="見積番号"
        value={params.quote_no || ""}
        type="input"
        onChange={handleTextChange("quote_no")}
        containerSx={itemContainerWidth}
      />
      <SearchItem
        label="お客様氏名"
        value={params.customer_name || ""}
        type="input"
        onChange={handleTextChange("customer_name")}
        containerSx={itemContainerWidth}
      />
      <SearchItem
        label="お客様電話番号"
        value={params.customer_tel_no || ""}
        type="input"
        onChange={handleTextChange("customer_tel_no")}
        containerSx={itemContainerWidth}
      />
      {getUserType() !== USER_TYPE_LIST.store.value && (
        <SearchItem
          label={
            getUserType() === USER_TYPE_LIST.company.value
              ? "店舗ID/店舗名"
              : "店舗ID/店舗名/企業名"
          }
          value={params.store_or_company || ""}
          type="input"
          onChange={handleTextChange("store_or_company")}
          containerSx={itemContainerWidth}
        />
      )}
      <SearchItem
        label="見積作成者"
        value={
          params.quote_by
            ? ({ label: params.quote_by, value: params.quote_by } as SearchItemAutocompleteOption)
            : null
        }
        type="autocomplete"
        autocompleteOptions={quoteByList}
        onChange={handleQuoteByChange}
        containerSx={itemContainerWidth}
      />
      <Box sx={{ position: "relative", width: { xs: "100%", lg: "auto" } }}>
        <Box sx={dateRowSx}>
          {/* Start Date */}
          <SearchItem
            label="作成日"
            type="date"
            value={params.created_at_start || null}
            maxDate={params.created_at_end || null}
            onChange={handleStartDateChange}
            containerSx={{}}
            valueSx={dateValueSx}
          />
          <Box sx={{ alignSelf: "center" }}>ー</Box>
          {/* End Date */}
          <SearchItem
            label=""
            labelSx={hiddenLabelSx}
            type="date"
            value={params.created_at_end || null}
            disabled={!params.created_at_start}
            minDate={params.created_at_start || null}
            onChange={handleEndDateChange}
            containerSx={{
              minWidth: { xs: "auto", md: "auto" },
            }}
            valueSx={dateValueSx}
          />
        </Box>
        <FormControlLabel
          sx={{
            position: "absolute",
            left: 40,
            top: 60,
            "& .MuiFormControlLabel-label": { fontSize: "18px", my: "auto" },
          }}
          control={<Checkbox color="secondary" />}
          label="有効期限切れは表示しない"
          checked={!!params.expired_flg}
          onChange={handleExpiredToggle}
        />
      </Box>
    </Box>
  );
}
